
import { defaultIpfsGateway, ipfsGateways } from '../config';

interface IPFSProcessingResult {
  primaryUrl: string | null;
  fallbackUrl: string | null;
  ipfsHash: string | null;
  ipfsPath: string | null;
}

// Filebase image optimization parameters
export interface FilebaseImageOptions {
  width?: number;
  height?: number;
  dpr?: number; // Device pixel ratio (1-5)
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  gravity?: 'auto' | 'center' | 'north' | 'south' | 'east' | 'west' | 'northeast' | 'northwest' | 'southeast' | 'southwest';
  quality?: number; // 1-100
  format?: 'auto' | 'jpeg' | 'png' | 'webp' | 'avif';
  sharpen?: number; // 0-10
  metadata?: 'keep' | 'copyright' | 'none';
  anim?: boolean; // false to reduce frames for GIFs
}

// Responsive image breakpoints for srcset generation
export interface ResponsiveImageOptions {
  baseWidth: number;
  maxWidth?: number;
  breakpoints?: number[]; // Custom breakpoints, defaults to standard set
  quality?: number;
  format?: 'auto' | 'webp' | 'jpeg' | 'png';
  dpr?: number[];
}

// Our Filebase gateway URL
const FILEBASE_GATEWAY = defaultIpfsGateway || 'https://ipfs.chewy.build/ipfs/';

/**
 * Process an IPFS URL into HTTP gateway URLs
 * @param url The original URL (ipfs://, http with /ipfs/, or direct hash)
 * @param preferredGateway Optional preferred gateway to use as primary
 */
export function processIPFSUrl(url: string, preferredGateway?: string): IPFSProcessingResult {
  if (!url) {
    return { primaryUrl: null, fallbackUrl: null, ipfsHash: null, ipfsPath: null };
  }

  // Already a standard HTTP URL (not IPFS)
  if (url.startsWith('http') && !url.includes('/ipfs/')) {
    return { primaryUrl: url, fallbackUrl: null, ipfsHash: null, ipfsPath: null };
  }

  let ipfsPath = '';
  let ipfsHash = '';

  // Extract IPFS hash and path from various formats
  if (url.startsWith('ipfs://')) {
    ipfsPath = url.replace('ipfs://', '');
  } else if (url.startsWith('ipfs/')) {
    ipfsPath = url.replace('ipfs/', '');
  } else if (url.includes('/ipfs/')) {
    const ipfsIndex = url.indexOf('/ipfs/');
    ipfsPath = url.substring(ipfsIndex + 6);
  } else if (url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/)) {
    ipfsPath = url;
  } else if (url.match(/^[a-z2-7]{59}$/)) {
    ipfsPath = url;
  } else if (url.match(/^bafy[a-z2-7]{55}$/)) {
    ipfsPath = url;
  } else {
    // Not an IPFS URL
    return { primaryUrl: url, fallbackUrl: null, ipfsHash: null, ipfsPath: null };
  }

  // Extract just the hash part for validation
  ipfsHash = ipfsPath.split('/')[0];

  // Validate hash format
  if (!ipfsHash || (!ipfsHash.startsWith('Qm') && !ipfsHash.match(/^[a-z2-7]{59}$/) && !ipfsHash.startsWith('bafy'))) {
    console.warn('⚠️ Invalid IPFS hash format:', ipfsHash, 'from URL:', url);
    return { primaryUrl: url, fallbackUrl: null, ipfsHash: null, ipfsPath: null };
  }

  // Use provided gateways or fallback to defaults
  const gateways = ipfsGateways

  // If preferred gateway is provided, use it as primary
  let primaryGateway = preferredGateway || gateways[0];
  let fallbackGateway = gateways.find(g => g !== primaryGateway) || gateways[1];

  const primaryUrl = `${primaryGateway}${ipfsPath}`;
  const fallbackUrl = `${fallbackGateway}${ipfsPath}`;

  return {
    primaryUrl,
    fallbackUrl,
    ipfsHash,
    ipfsPath
  };
}

/**
 * Build query string from Filebase image options
 */
function buildFilebaseQueryString(options: FilebaseImageOptions): string {
  const params = new URLSearchParams();

  if (options.width) params.append('img-width', options.width.toString());
  if (options.height) params.append('img-height', options.height.toString());
  if (options.dpr) params.append('img-dpr', options.dpr.toString());
  if (options.fit) params.append('img-fit', options.fit);
  if (options.gravity) params.append('img-gravity', options.gravity);
  if (options.quality) params.append('img-quality', options.quality.toString());
  if (options.format) params.append('img-format', options.format);
  if (options.sharpen) params.append('img-sharpen', options.sharpen.toString());
  if (options.metadata) params.append('img-metadata', options.metadata);
  if (options.anim === false) params.append('img-anim', 'false');

  return params.toString();
}

/**
 * Convert IPFS URL to optimized Filebase gateway URL with image parameters
 * Only applies optimizations when using our Filebase gateway
 */
export function getOptimizedIPFSUrl(
  url: string,
  options?: FilebaseImageOptions,
  fallbackGateway?: string
): string {
  if (!url) return '';

  const processed = processIPFSUrl(url);
  if (!processed.ipfsPath) return url;

  // Determine which gateway to use
  const isFilebaseGateway = !fallbackGateway || fallbackGateway === FILEBASE_GATEWAY;
  const targetGateway = fallbackGateway || FILEBASE_GATEWAY;

  let optimizedUrl = `${targetGateway}${processed.ipfsPath}`;

  // Only apply Filebase optimizations if using our gateway
  if (isFilebaseGateway && options) {
    const queryString = buildFilebaseQueryString(options);
    if (queryString) {
      optimizedUrl += `?${queryString}`;
    }
  }

  return optimizedUrl;
}

/**
 * Generate responsive srcset for IPFS images using Filebase optimizations
 * Only generates optimized srcset when using Filebase gateway
 */
export function generateResponsiveSrcSet(
  url: string,
  options: ResponsiveImageOptions
): { src: string; srcSet: string; sizes: string } {
  if (!url) return { src: '', srcSet: '', sizes: '' };

  const processed = processIPFSUrl(url);
  if (!processed.ipfsPath) return { src: url, srcSet: '', sizes: '' };

  const {
    baseWidth,
    maxWidth = 2048,
    breakpoints = [320, 480, 640, 768, 1024, 1280, 1536, 1920],
    quality = 80,
    format = 'auto',
    dpr = [1, 2, 3]
  } = options;

  // Base image URL
  const baseOptions: FilebaseImageOptions = {
    width: baseWidth,
    quality,
    format
  };
  const src = getOptimizedIPFSUrl(url, baseOptions);

  // Check if we're using Filebase gateway for optimizations
  const usingFilebase = processed.primaryUrl?.includes(FILEBASE_GATEWAY);

  if (!usingFilebase) {
    // Fallback to simple src without optimizations
    return { src: processed.primaryUrl || url, srcSet: '', sizes: '' };
  }

  // Generate srcset entries
  const srcSetEntries: string[] = [];

  // Generate width-based srcset
  breakpoints
    .filter(width => width <= maxWidth)
    .forEach(width => {
      dpr.forEach(dprValue => {
        const effectiveWidth = width * dprValue;
        if (effectiveWidth <= maxWidth) {
          const imgOptions: FilebaseImageOptions = {
            width: effectiveWidth,
            quality: Math.max(20, quality - (dprValue - 1) * 15), // Reduce quality for higher DPR
            format,
            dpr: dprValue
          };
          const optimizedUrl = getOptimizedIPFSUrl(url, imgOptions);
          srcSetEntries.push(`${optimizedUrl} ${effectiveWidth}w`);
        }
      });
    });

  // Generate sizes attribute (responsive breakpoints)
  const sizes = [
    '(max-width: 320px) 320px',
    '(max-width: 480px) 480px',
    '(max-width: 640px) 640px',
    '(max-width: 768px) 768px',
    '(max-width: 1024px) 1024px',
    '(max-width: 1280px) 1280px',
    '(max-width: 1536px) 1536px',
    '100vw'
  ].join(', ');

  return {
    src,
    srcSet: srcSetEntries.join(', '),
    sizes
  };
}

/**
 * Test if a URL is an IPFS URL
 */
export function isIPFSUrl(url: string): boolean {
  if (!url) return false;

  return url.startsWith('ipfs://') ||
    url.startsWith('ipfs/') ||
    url.includes('/ipfs/') ||
    url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/) !== null ||
    url.match(/^[a-z2-7]{59}$/) !== null ||
    url.match(/^bafy[a-z2-7]{55}$/) !== null;
}

/**
 * Get IPFS hash from URL if present
 */
export function getIPFSHashFromUrl(url: string): string | null {
  const result = processIPFSUrl(url);
  return result.ipfsHash;
}

/**
 * Convert any URL to use a specific IPFS gateway
 */
export function convertToGatewayUrl(url: string, gateway: string): string {
  const result = processIPFSUrl(url);
  if (result.ipfsPath) {
    return `${gateway}${result.ipfsPath}`;
  }
  return url;
}

/**
 * Check if URL is using Filebase gateway
 */
export function isFilebaseGateway(url: string): boolean {
  return url.includes('ipfs.chewy.build') || url.includes('filebase');
}

/**
 * Get optimized image URL with common optimization defaults
 */
export function getOptimizedImageUrl(
  url: string,
  width?: number,
  height?: number,
  quality: number = 80,
  format: 'auto' | 'webp' | 'jpeg' | 'png' = 'auto',
  fit: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad' = 'scale-down'
): string {
  return getOptimizedIPFSUrl(url, {
    width,
    height,
    quality,
    format: 'auto', // Auto-selects best format (WebP, AVIF, etc.)
    fit,
    metadata: 'none'
    // sharpen: 0.3 // Light sharpening
  });
}
